import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { Input } from '@components/core-ui/form-elements/Input';
import PhoneIcon from '@components/icons/Phone.svg';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import type { VerifyPhoneBodyDto } from 'app/API';
import React, { createRef, useEffect, useRef, useState } from 'react';
import { sendOtpCode } from '../../../../api/sendOtpCode';
import { verifyPhone } from '../../../../api/verifyPhone';
import { getRequestErrorMessage } from '../../../../helpers/getRequestErrorMessage';
import { isLocalTestHost } from '../../../../helpers/isLocalTestHost';
import { usePageContext } from '../../../../providers/PageProvider';

export const RegistrationPageCodeStage = () => {
  const { call } = useAPI();
  const {
    isLoading: isRegistering,
    phone,
    codeSentDate,
    setCodeSentDate,
    setOTPCode,
    error,
    setError,
    goToLogin,
    register,
  } = usePageContext();
  const { isPending: isVerifying, mutateAsync: verifyCode } = useMutation({
    mutationFn: (requestBody: VerifyPhoneBodyDto) => verifyPhone({ call, requestBody }),
  });
  const { isPending: isResending, mutateAsync: resendCode } = useMutation({
    mutationFn: (phoneNumber: string) => sendOtpCode({ call, requestBody: { phone: phoneNumber } }),
  });
  const isVerifyInFlight = useRef(false);
  const isResendInFlight = useRef(false);
  const isLoading = isRegistering || isVerifying || isResending;
  const RESEND_TIMEOUT = 60000;

  const [now, setNow] = useState(Date.now());
  const [code, setCode] = useState({
    code0: '',
    code1: '',
    code2: '',
    code3: '',
    code4: '',
    code5: '',
  });

  const codeRefs = Array.from({ length: 6 }, () => createRef<HTMLInputElement>());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFormSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (isVerifyInFlight.current) {
      return;
    }
    isVerifyInFlight.current = true;
    setError('');
    const fullCode = `${code.code0[0]}${code.code1[0]}${code.code2[0]}${code.code3[0]}${code.code4[0]}${code.code5[0]}`;
    setOTPCode(fullCode);

    try {
      const verifyResponse = await verifyCode({ phone, code: fullCode });

      if (!verifyResponse.result && !isLocalTestHost(window.location.hostname)) {
        setError('The code is invalid');
        return;
      }

      await register();
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError));
    } finally {
      isVerifyInFlight.current = false;
    }
  };

  const resend = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (codeSentDate > Date.now() - RESEND_TIMEOUT) {
      console.log('Only once a minute!');
      return;
    }
    if (isResendInFlight.current) {
      return;
    }
    isResendInFlight.current = true;

    try {
      await resendCode(phone);
      setCodeSentDate(Date.now());
      setCode({
        code0: '',
        code1: '',
        code2: '',
        code3: '',
        code4: '',
        code5: '',
      });
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError));
    } finally {
      isResendInFlight.current = false;
    }
  };

  const onCodeChange = (
    evt: React.ChangeEvent<
      | HTMLInputElement
      | {
          name: string;
          value: string;
        }
    >,
  ) => {
    setError('');
    const { name, value } = evt.target;
    setCode({ ...code, [name]: value[0] });
  };

  const isButtonDisabled = !phone?.length || !code.code5;

  const moveCodeFocus = (evt: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (evt.key === 'Backspace' && index > 0) {
      codeRefs[index - 1].current?.focus();
      return;
    }
    if (index < 5) {
      codeRefs[index + 1].current?.focus();
    }
  };

  return (
    <div className="root">
      <h1 className="header">Verify Your Phone Number</h1>

      <form onSubmit={handleFormSubmit}>
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <h2 className="enterPhoneNumber">Enter the verification code that has been sent to</h2>
        <div className="margin20">
          <Input
            onChange={() => {}}
            name="phone"
            label="Phone number"
            type="phone"
            value={phone || ''}
            icon={PhoneIcon}
            disabled={true}
          />
        </div>
        <div className="margin20 flexCode">
          <Input
            onChange={onCodeChange}
            name="code0"
            label="X"
            value={code.code0 || ''}
            disabled={isLoading}
            ref={codeRefs[0]}
            onKeyUp={(evt) => moveCodeFocus(evt, 0)}
          />
          -
          <Input
            onChange={onCodeChange}
            name="code1"
            label="X"
            value={code.code1 || ''}
            disabled={isLoading || !code.code0}
            ref={codeRefs[1]}
            onKeyUp={(evt) => moveCodeFocus(evt, 1)}
          />
          -
          <Input
            onChange={onCodeChange}
            name="code2"
            label="X"
            value={code.code2 || ''}
            disabled={isLoading || !code.code1}
            ref={codeRefs[2]}
            onKeyUp={(evt) => moveCodeFocus(evt, 2)}
          />
          -
          <Input
            onChange={onCodeChange}
            name="code3"
            label="X"
            value={code.code3 || ''}
            disabled={isLoading || !code.code2}
            ref={codeRefs[3]}
            onKeyUp={(evt) => moveCodeFocus(evt, 3)}
          />
          -
          <Input
            onChange={onCodeChange}
            name="code4"
            label="X"
            value={code.code4 || ''}
            disabled={isLoading || !code.code3}
            ref={codeRefs[4]}
            onKeyUp={(evt) => moveCodeFocus(evt, 4)}
          />
          -
          <Input
            onChange={onCodeChange}
            name="code5"
            label="X"
            value={code.code5 || ''}
            disabled={isLoading || !code.code4}
            ref={codeRefs[5]}
            onKeyUp={(evt) => moveCodeFocus(evt, 5)}
          />
        </div>

        <ButtonMedium disabled={isButtonDisabled} className="mb-[30px] mx-auto w-full" type="submit">
          Verify
        </ButtonMedium>

        {codeSentDate < now - RESEND_TIMEOUT ? (
          <p className="haveAnAccount">
            Did not receive a code?&nbsp;&nbsp;
            <span onClick={resend} className="signIn">
              Resend
            </span>
          </p>
        ) : null}

        {error ? (
          <div className="formError" data-testid="errorMessage">
            <ErrorMessage error={error} />
          </div>
        ) : null}
      </form>

      <p className="haveAnAccount">
        Have an account?&nbsp;&nbsp;
        <span onClick={goToLogin} className="signIn">
          Sign In
        </span>
      </p>
    </div>
  );
};
